import React from "react";
import { Reveal, Overline } from "@/components/Reveal";
import { PageHero, Section, Button } from "@/components/UI";
import { Slogan } from "@/components/Slogan";
import { IMG } from "@/lib/images";
import { Stethoscope, FileCheck, Share2, ShieldCheck } from "lucide-react";

const REFERRAL = [
  { t: "Oncology & Breast Reconstruction", d: "3D areola and nipple restoration as a final stage of the reconstructive journey after mastectomy." },
  { t: "Plastic & Reconstructive Surgery", d: "Scar camouflage and refinement following surgery, once healing is complete." },
  { t: "Dermatology", d: "Vitiligo camouflage and support for patients managing pigment disorders." },
  { t: "Burn & Trauma Care", d: "Restorative camouflage in coordination with burn units and trauma teams." },
];

export default function Healthcare() {
  return (
    <>
      <PageHero overline="For Healthcare Professionals" testId="healthcare-hero"
        title="A trusted referral partner for your patients"
        subtitle="CCPM helps physicians, nurses, and allied health professionals connect patients with verified, certified practitioners for restorative micropigmentation — with confidence in safety and standards." />

      <Section className="py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <Reveal><div className="overflow-hidden aspect-[4/5]"><img src={IMG.portrait} alt="Healthcare professional" className="w-full h-full object-cover" /></div></Reveal>
          <Reveal delay={0.1}>
            <Overline>Why refer to a CCPM practitioner</Overline>
            <div className="mt-8 space-y-8">
              {[
                { icon: ShieldCheck, t: "Verified against a national standard", d: "Every registry practitioner meets shared standards of practice and infection control." },
                { icon: Stethoscope, t: "Clinically grounded", d: "Trauma-informed care and clear scope, coordinated with the patient's care team." },
                { icon: FileCheck, t: "Documented & accountable", d: "Consent, records, and a confidential public complaints procedure." },
              ].map((f, i) => (
                <div key={i} className="flex gap-5">
                  <f.icon strokeWidth={1.3} size={28} className="text-ccpm-olive shrink-0" />
                  <div><h3 className="font-serif text-2xl text-ccpm-charcoal">{f.t}</h3><p className="mt-2 text-ccpm-taupe font-light leading-relaxed">{f.d}</p></div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </Section>

      {/* BRAND SLOGAN */}
      <Slogan variant="charcoal" testId="healthcare-slogan"
        quote="Color is not simply chosen."
        emphasis="It is observed, understood, formulated and refined." />

      <div className="bg-ccpm-stone py-20 lg:py-28">
        <Section>
          <Reveal><Overline>Common Referral Pathways</Overline></Reveal>
          <div className="grid md:grid-cols-2 gap-10 mt-10">
            {REFERRAL.map((r, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="border-t border-ccpm-border pt-6">
                  <h3 className="font-serif text-2xl text-ccpm-charcoal">{r.t}</h3>
                  <p className="mt-3 text-ccpm-taupe font-light leading-relaxed">{r.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Section>
      </div>

      <Section className="py-20 lg:py-28"><Reveal><div className="bg-ccpm-olive text-ccpm-ivory p-10 lg:p-16 grain relative">
        <div className="relative grid lg:grid-cols-2 gap-8 items-center">
          <div><Share2 strokeWidth={1.3} className="mb-5" /><h2 className="font-serif text-4xl leading-tight">Refer a patient or request information</h2><p className="mt-4 text-ccpm-ivory/80 font-light max-w-md">Our office can help you locate a verified practitioner or provide standards documentation for your clinic.</p></div>
          <div className="flex flex-wrap gap-4 lg:justify-end">
            <Button to="/registry" variant="light" data-testid="healthcare-registry">Search the registry</Button>
            <Button to="/contact" variant="outline" className="!border-ccpm-ivory !text-ccpm-ivory hover:!bg-ccpm-ivory hover:!text-ccpm-olive" data-testid="healthcare-contact">Contact the office</Button>
          </div>
        </div>
      </div></Reveal></Section>
    </>
  );
}
